import type { LibraryTemplate } from '@headline/schemas'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { FilterPills } from '@/components/FilterPills'
import { LibraryTable, type LibraryColumn } from '@/components/LibraryTable'
import { PageHeader } from '@/components/PageHeader'
import { TemplateStartersBanner } from '@/components/accounts/TemplateStartersBanner'
import { TemplateDeleteImpact, TemplateUsedIn } from '@/components/accounts/TemplateUsedIn'
import { templateUsage } from '@/components/accounts/template-usage'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { CopyTemplateDialog } from '@/components/templates/CopyTemplateDialog'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  accountsQuery,
  allTemplatesQuery,
  reportsOverviewQuery,
  useCreateSharedTemplate,
  useDeleteReportTemplate,
  useDeleteSharedTemplate,
  useSetTemplateOwner,
} from '@/lib/queries'

/**
 * The internal Template Library — every template across Clipbook: the shared library plus each
 * account's own. Staff-only (`templates:manage`). See all, filter by owner scope, search, create
 * (shared), delete (any), and "assign" via the Owner column (which MOVES a template's single
 * owner — the only assignment today's model supports; multi-assign and sub-account scope need a
 * data-model change, deferred). Table chrome is the shared LibraryTable, styled after coverage lists.
 */
export const Route = createFileRoute('/app/templates/')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(allTemplatesQuery()),
      context.queryClient.ensureQueryData(accountsQuery),
      context.queryClient.ensureQueryData(reportsOverviewQuery),
    ]),
  component: TemplateLibraryPage,
})

/** The columns, closed over the page's data + handlers. The Owner cell doubles as the "Assign to…"
 *  control: a select whose value IS the current owner, so changing it re-homes the template. */
function templateColumns({
  accounts,
  usedIn,
  busy,
  onAssign,
  onCopy,
  onDelete,
}: {
  accounts: Array<{ id: string; name: string }>
  usedIn: Map<string, string[]>
  busy: boolean
  onAssign: (template: LibraryTemplate, accountId: string | null) => void
  onCopy: (template: LibraryTemplate) => void
  onDelete: (template: LibraryTemplate) => void
}): Array<LibraryColumn<LibraryTemplate>> {
  return [
    {
      key: 'name',
      header: 'Template',
      cell: (template) =>
        template.accountId === null ? (
          <span className="inline-flex items-center gap-2">
            <Link
              to="/app/templates/$templateId"
              params={{ templateId: template.id }}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              {template.name}
            </Link>
            {/* The one row most reports render through, named so nobody edits it as "a template". */}
            {template.role === 'standard' && (
              <span
                title="Every report without a template renders through this layout."
                className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
              >
                Standard layout
              </span>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2">
            <Link
              to="/app/accounts/$accountId/templates/$templateId"
              params={{ accountId: template.accountId, templateId: template.id }}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              {template.name}
            </Link>
          </span>
        ),
    },
    {
      key: 'owner',
      header: 'Owner',
      cell: (template) =>
        // The standard layout is resolved from the library by role; moving it would hand every
        // untemplated report back to the built-in copy, and the server refuses it anyway.
        template.role === 'standard' ? (
          <span className="text-sm text-muted-foreground">Library (shared)</span>
        ) : (
          <SearchableSelect
            ariaLabel={`Owner of ${template.name}`}
            value={template.accountId ?? ''}
            onValueChange={(next) => onAssign(template, next || null)}
            options={[
              { value: '', label: 'Library (shared)' },
              ...accounts.map((account) => ({
                value: account.id,
                label: account.name,
                group: 'Accounts',
              })),
            ]}
            searchPlaceholder="Account name…"
            className="w-56"
          />
        ),
    },
    {
      key: 'usedIn',
      header: 'Used in',
      headClassName: 'text-right',
      cellClassName: 'text-right tabular-nums',
      cell: (template) => <TemplateUsedIn reports={usedIn.get(template.id) ?? []} />,
    },
    {
      key: 'actions',
      header: '',
      cellClassName: 'text-right',
      cell: (template) => (
        <div className="flex items-center justify-end gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => onCopy(template)}>
            Copy
          </Button>
          {/* Nothing to fall back to if the standard layout went: the server answers 409. */}
          {template.role !== 'standard' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive"
              disabled={busy}
              onClick={() => onDelete(template)}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ]
}

function TemplateLibraryPage() {
  const queryClient = useQueryClient()
  const { data: all } = useSuspenseQuery(allTemplatesQuery())
  const { data: accountsData } = useSuspenseQuery(accountsQuery)
  const { data: overview } = useSuspenseQuery(reportsOverviewQuery)
  const create = useCreateSharedTemplate(queryClient)
  const setOwner = useSetTemplateOwner(queryClient)
  const deleteShared = useDeleteSharedTemplate(queryClient)
  const deleteAccountTemplate = useDeleteReportTemplate(queryClient)
  const [scope, setScope] = useState<TemplateScope>('all')
  const [copyTarget, setCopyTarget] = useState<LibraryTemplate | null>(null)
  // A report with no template renders through the standard row, so it counts as using it.
  const standardId = all.templates.find((template) => template.role === 'standard')?.id ?? null
  const usedIn = templateUsage(overview.reports, standardId)
  const accounts = accountsData.accounts.map((account) => ({ id: account.id, name: account.name }))
  const libraryNames = all.templates
    .filter((template) => template.accountId === null)
    .map((template) => template.name)

  const rows = scopedTemplates(all.templates, scope)

  // Both deletes go through the account-scoped or shared route by owner; neither invalidates the
  // library list on its own, so refresh it here.
  const refreshLibrary = () => queryClient.invalidateQueries({ queryKey: ['all-report-templates'] })
  const onAssign = (template: LibraryTemplate, accountId: string | null) =>
    setOwner.mutate({ templateId: template.id, accountId })
  // Delete confirms first, naming the reports still using it (Phoebe's Report Config feedback).
  const [pendingDelete, setPendingDelete] = useState<LibraryTemplate | null>(null)
  const done = () => {
    setPendingDelete(null)
    void refreshLibrary()
  }
  const confirmDelete = (template: LibraryTemplate) =>
    template.accountId === null
      ? deleteShared.mutate({ templateId: template.id }, { onSuccess: done })
      : deleteAccountTemplate.mutate(
          { accountId: template.accountId, templateId: template.id },
          { onSuccess: done },
        )
  const doomedUses = pendingDelete ? (usedIn.get(pendingDelete.id) ?? []) : []

  const busy = setOwner.isPending || deleteShared.isPending || deleteAccountTemplate.isPending
  // The first failure to report, in this order: owner move, either delete, then create.
  const error = [setOwner, deleteShared, deleteAccountTemplate, create]
    .map((mutation) => mutation.error?.message)
    .find((message) => message !== undefined)

  const columns = templateColumns({
    accounts,
    usedIn,
    busy,
    onAssign,
    onCopy: setCopyTarget,
    onDelete: setPendingDelete,
  })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Template Library"
          description="Every template across Clipbook — the shared library plus each account's own. Colour and typeface come from the account a report belongs to, so one layout renders in every client's brand."
        />
        <NewTemplateButton create={create} templates={all.templates} />
      </div>

      <TemplateStartersBanner sharedTemplateNames={libraryNames} />

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <LibraryTable
        rows={rows}
        filtered={scope !== 'all'}
        columns={columns}
        rowKey={(template) => template.id}
        searchText={(template) => `${template.name} ${ownerLabel(template)}`}
        searchPlaceholder="Search templates…"
        filters={
          <FilterPills
            options={[
              { value: 'all', label: 'All owners' },
              { value: 'library', label: 'Shared library' },
              { value: 'accounts', label: 'Account-owned' },
            ]}
            value={scope}
            onChange={setScope}
          />
        }
        emptyAll="No templates anywhere yet. One created here belongs to the shared library — any report, in any account, can pick it."
        emptyFiltered="No templates match."
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        tone="destructive"
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete template?'}
        confirmLabel="Delete template"
        confirmIcon={<Trash2 className="size-4" />}
        pending={deleteShared.isPending || deleteAccountTemplate.isPending}
        pendingLabel="Deleting…"
        description="This can’t be undone."
        body={<TemplateDeleteImpact reports={doomedUses} />}
        onConfirm={() => {
          if (pendingDelete) confirmDelete(pendingDelete)
        }}
      />

      {copyTarget && (
        <CopyTemplateDialog
          template={copyTarget}
          accounts={accounts}
          onClose={() => setCopyTarget(null)}
          queryClient={queryClient}
        />
      )}
    </div>
  )
}

type TemplateScope = 'all' | 'library' | 'accounts'

// The standard layout first — it is the row most reports render through — then the rest of the
// shared library, then each account's own; within a group, the order they were created in.
const rank = (template: LibraryTemplate) =>
  template.role === 'standard' ? 0 : template.accountId === null ? 1 : 2

/** The library in display order, narrowed to the owner scope the pills select. */
function scopedTemplates(templates: LibraryTemplate[], scope: TemplateScope): LibraryTemplate[] {
  const ordered = templates.toSorted((a, b) => rank(a) - rank(b))
  if (scope === 'all') return ordered
  return ordered.filter((template) =>
    scope === 'library' ? template.accountId === null : template.accountId !== null,
  )
}

/** Creates the next "Untitled template" in the shared library and opens it. */
function NewTemplateButton({
  create,
  templates,
}: {
  create: ReturnType<typeof useCreateSharedTemplate>
  templates: LibraryTemplate[]
}) {
  const navigate = useNavigate()
  return (
    <Button
      disabled={create.isPending}
      onClick={() =>
        create.mutate(
          { name: nextUntitledLibraryName(templates) },
          {
            onSuccess: (template) =>
              void navigate({
                to: '/app/templates/$templateId',
                params: { templateId: template.id },
              }),
          },
        )
      }
    >
      {create.isPending ? 'Creating…' : 'New template'}
    </Button>
  )
}

/** Owner label for search: an account's name, or "library" for shared rows. */
const ownerLabel = (template: LibraryTemplate) => template.accountName ?? 'library'

/** The next free "Untitled template [n]", measured against the library's own names. */
function nextUntitledLibraryName(templates: LibraryTemplate[]): string {
  const taken = new Set(
    templates.filter((template) => template.accountId === null).map((template) => template.name),
  )
  if (!taken.has('Untitled template')) return 'Untitled template'
  let n = 2
  while (taken.has(`Untitled template ${n}`)) n += 1
  return `Untitled template ${n}`
}
